import qrcode from 'qrcode-generator';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { Brand } from '../components/brand';
import { ThemeToggle } from '../components/theme';
import { Button, ErrorBox, Field, Input, Notice } from '../components/ui';
import { setLanguage } from '../i18n';
import { authCall } from '../lib/rpc';

function Shell({ title, children }: { title: string; children: ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-start gap-3">
          <Brand large />
          <span className="ml-auto flex shrink-0 flex-col items-end gap-2">
            <span className="flex gap-1">
              {(['de', 'en', 'tr'] as const).map((l) => (
                <button key={l} onClick={() => setLanguage(l)} className={`rounded px-1.5 text-xs uppercase ${i18n.language === l ? 'bg-ink text-sheet' : 'text-graphite'}`}>
                  {l}
                </button>
              ))}
            </span>
            <ThemeToggle />
          </span>
        </div>
        <div className="rounded-lg border border-line bg-sheet p-6">
          <h1 className="mb-4 text-lg font-semibold">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage({ entraEnabled, onDone }: { entraEnabled: boolean; onDone: () => void }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const entraError = params.get('error');
  return (
    <Shell title={t('auth.title')}>
      {entraError === 'entra_unknown' && <Notice tone="bad" className="mb-3">{t('auth.entraUnknown')}</Notice>}
      {entraError === 'entra_failed' && <Notice tone="bad" className="mb-3">{t('auth.entraFailed')}</Notice>}
      {entraError === 'entra_mismatch' && <Notice tone="bad" className="mb-3">{t('auth.entraMismatch')}</Notice>}
      {entraEnabled && (
        <>
          <a href="/api/auth/entra/start">
            <Button variant="primary" className="w-full">
              {t('auth.signInMicrosoft')}
            </Button>
          </a>
          <div className="my-4 text-center text-xs text-graphite">{t('auth.or')}</div>
        </>
      )}
      <form
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            const r = await authCall<{ step: string }>('login', { email, password, totp: needTotp ? totp : undefined });
            if (r.step === 'totp') setNeedTotp(true);
            else onDone();
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label={t('auth.email')} htmlFor="login-email">
          <Input id="login-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t('auth.password')} htmlFor="login-password">
          <Input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {needTotp && (
          <Field label={t('auth.totp')} htmlFor="login-totp">
            <Input id="login-totp" inputMode="numeric" autoComplete="one-time-code" autoFocus value={totp} onChange={(e) => setTotp(e.target.value)} />
          </Field>
        )}
        <ErrorBox error={error} />
        <Button type="submit" variant={entraEnabled ? 'secondary' : 'primary'} loading={busy} disabled={!email || !password}>
          {t('auth.signIn')}
        </Button>
      </form>
      <p className="mt-4 text-xs text-graphite">{t('auth.noAccess')}</p>
    </Shell>
  );
}

export function SetupPage({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [info, setInfo] = useState<{ email: string; displayName: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    authCall<{ email: string; displayName: string }>(`setup/${encodeURIComponent(token)}`, undefined, 'GET').then(setInfo).catch(setError);
  }, [token]);
  return (
    <Shell title={t('auth.setupTitle')}>
      {info && <p className="mb-4 text-sm text-graphite">{t('auth.setupFor', { name: info.displayName, email: info.email })}</p>}
      {info && (
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              await authCall('setup', { token, password });
              window.location.hash = '#/';
              onDone();
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label={t('auth.setPassword')} hint={t('auth.passwordRules')}>
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" loading={busy} disabled={password.length < 10}>
            {t('common.save')}
          </Button>
        </form>
      )}
      <ErrorBox error={error} />
    </Shell>
  );
}

export function TotpSetupPage({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [secret, setSecret] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    authCall<{ secret: string; otpauthUrl: string }>('totp/setup').then(setSecret).catch(setError);
  }, []);
  const svg = useMemo(() => {
    if (!secret) return '';
    const qr = qrcode(0, 'M');
    qr.addData(secret.otpauthUrl);
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [secret]);
  return (
    <Shell title={t('auth.totpSetupTitle')}>
      <p className="mb-4 text-sm text-graphite">{t('auth.totpSetupText')}</p>
      {secret && (
        <>
          <div className="mx-auto mb-3 w-48 rounded bg-white p-2" dangerouslySetInnerHTML={{ __html: svg }} />
          <Field label={t('auth.totpManual')}>
            <code className="block rounded bg-sheet-2 px-2 py-1 text-xs break-all">{secret.secret}</code>
          </Field>
          <form
            className="mt-3 flex flex-col gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                await authCall('totp/enable', { code });
                onDone();
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Field label={t('auth.totp')}>
              <Input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Button type="submit" variant="primary" loading={busy} disabled={code.length < 6}>
              {t('auth.enable')}
            </Button>
          </form>
        </>
      )}
      <ErrorBox error={error} />
    </Shell>
  );
}
